'use client';

/**
 * @fileoverview Client hook that sends a user message to the chat API,
 * consumes its NDJSON stream, and drives the UI state for a single turn.
 *
 * State model:
 *   - `messages`          — committed message history for the active session.
 *   - `streamingMessage`  — the assistant's in-flight message (text + live
 *                           tool-call indicators). Null when idle.
 *   - `isStreaming`       — true while a turn is in progress.
 *   - `error`             — terminal error from the flow, if any.
 *
 * The caller owns the `activeSessionId`. When the server creates a new
 * session (because the caller passed null), the hook invokes
 * `onSessionCreated(sessionId, title)` so the caller can hoist it into
 * its state and pass it back on the next turn.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '@/lib/auth-fetch';
import type {
  ChatMessage,
  ChatToolCall,
  ChatContextHint,
} from '@/domains/chat/schemas';

// =============================================================================
// STREAM CHUNK TYPES (mirrors server; duplicated here to avoid importing
// the Zod schema client-side and dragging zod into the bundle for a simple
// shape the server promises.)
// =============================================================================

type TextChunk = { type: 'text'; delta: string };
type ToolStartChunk = {
  type: 'tool-call-start';
  toolCallId: string;
  name: string;
  input?: unknown;
};
type ToolEndChunk = {
  type: 'tool-call-end';
  toolCallId: string;
  error?: string;
};
type DoneChunk = { type: 'done'; assistantMessage: ChatMessage };
type SessionChunk = {
  type: 'session';
  sessionId: string;
  title: string;
  isNewSession: boolean;
};
type PersistErrorChunk = { type: 'persist-error'; message: string };
type ErrorChunk = { type: 'error'; message: string };

type AnyChunk =
  | TextChunk
  | ToolStartChunk
  | ToolEndChunk
  | DoneChunk
  | SessionChunk
  | PersistErrorChunk
  | ErrorChunk;

// =============================================================================
// STREAMING MESSAGE (in-flight assistant turn)
// =============================================================================

export interface StreamingAssistantMessage {
  id: string;
  content: string;
  /** Tool invocations currently running or completed, in order. */
  toolCalls: ChatToolCall[];
}

// =============================================================================
// HOOK
// =============================================================================

export interface UseChatStreamOptions {
  projectId: string;
  activeSessionId: string | null;
  onSessionCreated?: (sessionId: string, title: string) => void;
  /** Called after a turn is persisted server-side so the caller can bump summary counts. */
  onTurnPersisted?: (sessionId: string) => void;
}

export interface UseChatStreamResult {
  messages: ChatMessage[];
  streamingMessage: StreamingAssistantMessage | null;
  isStreaming: boolean;
  error: string | null;
  sendMessage: (text: string, contextHint?: ChatContextHint) => Promise<void>;
  /** Replaces the committed history (used when loading an existing session). */
  hydrate: (messages: ChatMessage[]) => void;
  /** Clears committed + streaming state (used when starting a fresh session). */
  clear: () => void;
}

export function useChatStream({
  projectId,
  activeSessionId,
  onSessionCreated,
  onTurnPersisted,
}: UseChatStreamOptions): UseChatStreamResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingMessage, setStreamingMessage] =
    useState<StreamingAssistantMessage | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard against state updates after unmount / session-switch mid-stream.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // Note: neither hydrate nor clear resets `error`. When a failed send
  // auto-creates a session, the panel reacts to the new activeSessionId
  // by calling hydrate([]) — we don't want that race to wipe the error
  // banner before the user can read it. `sendMessage` resets the error
  // on the next turn, so leaving it sticky here is safe.
  const hydrate = useCallback((next: ChatMessage[]) => {
    setMessages(next);
    setStreamingMessage(null);
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    setStreamingMessage(null);
  }, []);

  const sendMessage = useCallback(
    async (text: string, contextHint?: ChatContextHint) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      setError(null);
      setIsStreaming(true);

      // Optimistic user message. Server will persist its own copy with a
      // different id; we reconcile on 'done' by appending the authoritative
      // assistantMessage and keeping the user stub we already showed.
      const optimisticUser: ChatMessage = {
        id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimisticUser]);
      setStreamingMessage({
        id: `assistant-${Date.now()}`,
        content: '',
        toolCalls: [],
      });

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      try {
        const res = await authFetch(`/api/projects/${projectId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: activeSessionId,
            userMessage: trimmed,
            contextHint,
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          const body = await res.text().catch(() => '');
          throw new Error(body || `HTTP ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let sessionIdForPersist: string | null = activeSessionId;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Process complete lines (NDJSON boundary). Keep the trailing
          // partial line in the buffer for the next iteration.
          let newlineIdx: number;
          while ((newlineIdx = buffer.indexOf('\n')) >= 0) {
            const line = buffer.slice(0, newlineIdx).trim();
            buffer = buffer.slice(newlineIdx + 1);
            if (!line) continue;

            let chunk: AnyChunk;
            try {
              chunk = JSON.parse(line) as AnyChunk;
            } catch (parseErr) {
              console.warn('[useChatStream] Dropped unparseable line:', line, parseErr);
              continue;
            }

            if (chunk.type === 'session') {
              sessionIdForPersist = chunk.sessionId;
              if (chunk.isNewSession) {
                onSessionCreated?.(chunk.sessionId, chunk.title);
              }
              continue;
            }

            if (chunk.type === 'text') {
              setStreamingMessage((prev) =>
                prev ? { ...prev, content: prev.content + chunk.delta } : prev,
              );
              continue;
            }

            if (chunk.type === 'tool-call-start') {
              setStreamingMessage((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  toolCalls: [
                    ...prev.toolCalls,
                    {
                      id: chunk.toolCallId,
                      name: chunk.name,
                      input: chunk.input,
                      startedAt: new Date().toISOString(),
                    },
                  ],
                };
              });
              continue;
            }

            if (chunk.type === 'tool-call-end') {
              setStreamingMessage((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  toolCalls: prev.toolCalls.map((tc) =>
                    tc.id === chunk.toolCallId
                      ? {
                          ...tc,
                          completedAt: new Date().toISOString(),
                          error: chunk.error,
                        }
                      : tc,
                  ),
                };
              });
              continue;
            }

            if (chunk.type === 'done') {
              // Commit the authoritative assistant message and clear
              // the streaming buffer.
              setMessages((prev) => [...prev, chunk.assistantMessage]);
              setStreamingMessage(null);
              if (sessionIdForPersist) {
                onTurnPersisted?.(sessionIdForPersist);
              }
              continue;
            }

            if (chunk.type === 'persist-error') {
              // Assistant reply arrived but wasn't saved. Don't wipe
              // the streaming state — just surface a non-fatal error.
              setError(chunk.message);
              continue;
            }

            if (chunk.type === 'error') {
              throw new Error(chunk.message);
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        const message = (err as Error).message || 'Chat request failed.';
        // Log to console so the error survives UI state churn (e.g. a
        // freshly created session re-hydrating and wiping the error banner).
        console.error('[useChatStream] Chat turn failed:', err);
        setError(message);
        setStreamingMessage(null);
        // Roll back the optimistic user message on failure — the server
        // never persisted it, so keeping it would create a false history.
        setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
      } finally {
        setIsStreaming(false);
      }
    },
    [activeSessionId, isStreaming, onSessionCreated, onTurnPersisted, projectId],
  );

  return {
    messages,
    streamingMessage,
    isStreaming,
    error,
    sendMessage,
    hydrate,
    clear,
  };
}
